import { apiClient } from './api/client';
import { formatPrice } from '@/utils/formatters';

export interface Client {
  id: number;
  name: string;
  email: string;
  phone: string;
  address?: string;
  city?: string;
  postalCode?: string;
  orders: number;
  total: string;
  totalAmount: number;
  status: 'active' | 'inactive' | 'vip';
  lastOrderDate?: string;
}

interface ClientResponse {
  id: number;
  name: string;
  email: string;
  phone: string;
  address?: string;
  city?: string;
  postal_code?: string;
  total_orders: number;
  total_spent: number;
  status: 'active' | 'inactive' | 'vip';
  last_order_date?: string;
}

export const clientService = {
  /**
   * Récupère tous les clients depuis l'API
   */
  async getAll(filters?: {
    status?: string;
    search?: string;
    orders?: string;
    limit?: number;
    offset?: number;
  }): Promise<Client[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.orders) params.append('orders', filters.orders);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());
      
      const query = params.toString();
      const clients: ClientResponse[] = await apiClient.get<ClientResponse[]>(`/clients${query ? `?${query}` : ''}`);
      
      // Transformer les données de l'API en format Client
      return clients.map(client => ({
        id: client.id,
        name: client.name,
        email: client.email,
        phone: client.phone || '',
        address: client.address,
        city: client.city,
        postalCode: client.postal_code,
        orders: client.total_orders,
        total: formatPrice(client.total_spent),
        totalAmount: client.total_spent,
        status: client.status,
        lastOrderDate: client.last_order_date,
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération des clients:', error);
      throw error;
    }
  },

  /**
   * Récupère un client par ID
   */
  async getById(id: number): Promise<Client> {
    try {
      const client: ClientResponse = await apiClient.get<ClientResponse>(`/clients/${id}`);
      return {
        id: client.id,
        name: client.name,
        email: client.email,
        phone: client.phone || '',
        address: client.address,
        city: client.city,
        postalCode: client.postal_code,
        orders: client.total_orders,
        total: formatPrice(client.total_spent),
        totalAmount: client.total_spent,
        status: client.status,
        lastOrderDate: client.last_order_date,
      };
    } catch (error) {
      console.error('Erreur lors de la récupération du client:', error);
      throw error;
    }
  },

  /**
   * Récupère les statistiques des clients
   */
  async getStats(): Promise<{
    total: number;
    new: number;
    active: number;
    inactive: number;
    vip: number;
  }> {
    try {
      return await apiClient.get('/clients/stats');
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques clients:', error);
      throw error;
    }
  },

  /**
   * Met à jour un client
   */
  async update(id: number, data: {
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    postalCode?: string;
    status?: 'active' | 'inactive' | 'vip';
  }): Promise<Client> {
    try {
      const updateData: any = {};
      if (data.name !== undefined) updateData.name = data.name;
      if (data.email !== undefined) updateData.email = data.email;
      if (data.phone !== undefined) updateData.phone = data.phone;
      if (data.address !== undefined) updateData.address = data.address;
      if (data.city !== undefined) updateData.city = data.city;
      if (data.postalCode !== undefined) updateData.postal_code = data.postalCode;
      if (data.status !== undefined) updateData.status = data.status;

      const client: ClientResponse = await apiClient.put<ClientResponse>(`/clients/${id}`, updateData);
      return {
        id: client.id,
        name: client.name,
        email: client.email,
        phone: client.phone || '',
        address: client.address,
        city: client.city,
        postalCode: client.postal_code,
        orders: client.total_orders,
        total: formatPrice(client.total_spent),
        totalAmount: client.total_spent,
        status: client.status,
        lastOrderDate: client.last_order_date,
      };
    } catch (error) {
      console.error('Erreur lors de la mise à jour du client:', error);
      throw error;
    }
  },

  /**
   * Crée un nouveau client
   */
  async create(data: {
    name: string;
    email: string;
    phone: string;
    address?: string;
    city?: string;
    postalCode?: string;
    status?: 'active' | 'inactive' | 'vip';
  }): Promise<Client> {
    try {
      const createData: any = {
        name: data.name,
        email: data.email,
        phone: data.phone,
      };
      if (data.address !== undefined) createData.address = data.address;
      if (data.city !== undefined) createData.city = data.city;
      if (data.postalCode !== undefined) createData.postal_code = data.postalCode;
      if (data.status !== undefined) createData.status = data.status;

      const client: ClientResponse = await apiClient.post<ClientResponse>('/clients', createData);
      return {
        id: client.id,
        name: client.name,
        email: client.email,
        phone: client.phone || '',
        address: client.address,
        city: client.city,
        postalCode: client.postal_code,
        orders: client.total_orders,
        total: formatPrice(client.total_spent),
        totalAmount: client.total_spent,
        status: client.status,
        lastOrderDate: client.last_order_date,
      };
    } catch (error) {
      console.error('Erreur lors de la création du client:', error);
      throw error;
    }
  },

  /**
   * Supprime un client
   */
  async delete(id: number): Promise<void> {
    try {
      await apiClient.delete(`/clients/${id}`);
    } catch (error) {
      console.error('Erreur lors de la suppression du client:', error);
      throw error;
    }
  },
};


